import React from "react";

import { personName } from "@stepapp/core";
import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";
import { palette, spacing } from "../styles";
import Avatar from "./Avatar";
import Block from "./Block";
import IconButton from "./IconButton";
import ImagePicker from "./ImagePicker";
import Touchable from "./Touchable";
import Typography from "./Typography";

type Props = {
  description?: string;
  onPhotoSelect?: (base64: string) => void;
  onEdit?: () => void;
  navigateToProfile?: () => void;
  avatar?: string;
  firstName?: string;
  lastName?: string;
  type?: "default" | "small";
  marginTop?: number;
  isProfileScreen?: boolean;
  isProfileFilled?: boolean;
  style?: StyleProp<ViewStyle>;
  t: NotWorth;
};

const ProfileInfo = ({
  avatar,
  firstName,
  lastName,
  description,
  type = "default",
  onPhotoSelect,
  navigateToProfile,
  onEdit,
  isProfileScreen = true,
  isProfileFilled = true,
  marginTop = 2.5,
  style,
  t,
}: Props) => {
  const isSmall = type === "small";

  return (
    <Block row justifySpace marginBottom={3} marginTop={marginTop} style={style}>
      <Block alignCenter row>
        <Avatar size={spacing.unit * (isSmall ? 10 : 12)} uri={avatar ?? ""} />

        {isProfileScreen && (
          <Block marginLeft={2}>
            {onPhotoSelect ? (
              <ImagePicker onPhotoSelect={onPhotoSelect} t={t}>
                <Typography decoration="underline" type="accentLink" variant="bodyBold">
                  {t("common.choosePhoto")}
                </Typography>
              </ImagePicker>
            ) : (
              <>
                <Typography variant="title">
                  {firstName ? personName(firstName as string, lastName as string) : ""}
                </Typography>
                {description && <Typography type="secondary">{description}</Typography>}
              </>
            )}
          </Block>
        )}

        {navigateToProfile && (
          <Touchable onPress={navigateToProfile} style={styles.profileDrawer}>
            {firstName ? (
              <Block justifyCenter>
                <Typography variant="title">
                  {t("components.drawer.heyYou", { name: firstName })}
                </Typography>
                <Typography style={styles.showProfileText} type="accentLink" variant="buttonLink">
                  {isProfileFilled
                    ? t("components.drawer.showProfile")
                    : t("components.drawer.fillProfile")}
                </Typography>
              </Block>
            ) : (
              <Block testID="signIn">
                <Typography variant="sectionTitle">{t("components.drawer.hey")}</Typography>
                <Typography type="primaryLink" variant="buttonLink">
                  {t("screens.auth.logIn")}
                </Typography>
              </Block>
            )}
          </Touchable>
        )}
      </Block>

      {onEdit && (
        <IconButton
          name="edit"
          style={styles.editButton}
          iconColor={palette.common.white}
          iconSize={20}
          size={40}
          onPress={onEdit}
        />
      )}
    </Block>
  );
};

export default React.memo(ProfileInfo);

const styles = StyleSheet.create({
  container: {
    backgroundColor: palette.common.white,
    paddingTop: spacing.unit * 4,
  },
  editButton: {
    backgroundColor: palette.primary.accent,
  },
  profileDrawer: {
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
    padding: spacing.unit,
    paddingLeft: spacing.unit * 3,
  },
  showProfileText: {
    textDecorationLine: "underline",
    textDecorationStyle: "solid",
  },
});
